import { useGetAllLabWorksQuery } from '@pm/graphql';
import { ButtonLink, IconTile, InlineMessage, Text } from '@pm/ui';
import { useTranslation } from 'react-i18next';
import { Flag } from '../../components/FeatureFlag/FeatureFlag';
import { usePatientProfile } from '../../hooks/profile';
import { labsRoutes } from '../../modules/labs/labs-routes';
import { LoadingPage } from '../../pages/LoadingPage';
import { LabError } from './LabError';
import { LabHeroCard } from './LabHeroCard';
import {
  buildLinkedResultIndex,
  heroCardPropsFor,
  isActiveLabWork,
} from './labHeroCardUtils';
import { LabSupport } from './LabSupport';
import { PastLabs } from './PastLabs';

// The hero section is reserved for what the patient should act on *right now* —
// anything beyond this limit falls into the All Lab History list below so the
// top of the page stays focused. Mirrors the 1–2 card stacking in the Figma.
const HERO_CARD_LIMIT = 2;

export const LabsPage = ({ supportLink }: { supportLink: string }) => {
  const { isUsaPatient } = usePatientProfile();
  const { data, error, loading } = useGetAllLabWorksQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const { t } = useTranslation('labs');

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return <LabError error={error} supportLink={supportLink} />;
  }

  const allLabWorks = data?.labWorks?.edges || [];
  const noLabWorkYet = allLabWorks.length === 0;

  const allNodes = allLabWorks.map((edge) => edge.node);
  const linkedResultByAnchorId = buildLinkedResultIndex(allNodes);

  // CA patients only get the new lab-requisition hero (intentional)
  const activeHeroCards = allNodes
    .filter(isActiveLabWork)
    .filter((node) => isUsaPatient || node.__typename === 'LabRequisition')
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )
    .flatMap((node) => {
      const props = heroCardPropsFor(
        node,
        linkedResultByAnchorId.get(node.id) ?? null,
        supportLink,
        isUsaPatient,
      );
      return props ? [{ node, props }] : [];
    })
    .slice(0, HERO_CARD_LIMIT);

  const heroNodeKeys = new Set(
    activeHeroCards.flatMap(({ node }) => {
      const keys = [`${node.__typename}:${node.id}`];
      const linked = linkedResultByAnchorId.get(node.id);
      if (linked) keys.push(`${linked.__typename}:${linked.id}`);
      return keys;
    }),
  );

  const remainingLabWorks = allLabWorks.filter(
    (edge) => !heroNodeKeys.has(`${edge.node.__typename}:${edge.node.id}`),
  );
  const displayedLabWorks = remainingLabWorks.slice(0, 3);
  // View all must also surface when the server paginates — a small current
  // slice can still hide older labs on subsequent pages.
  const hasMoreLabs =
    remainingLabWorks.length > 3 ||
    (data?.labWorks?.pageInfo.hasNextPage ?? false);

  return (
    <div className="space-y-s">
      <Flag flag="hivTestBanner">
        <InlineMessage
          intent="warning"
          instructions={t('ShortageBanner.Content')}
        />
      </Flag>

      <div className="space-y-xxs">
        <Text size="headingM" as="h1">
          {t('Title')}
        </Text>
        <Text size="bodyL">{t('SubTitle')}</Text>
      </div>

      {noLabWorkYet ? (
        <div>
          <NoLabWorkYet />
        </div>
      ) : (
        <div>
          <div className="space-y-xxl pt-s">
            <div className="space-y-xl">
              <div className="space-y-xs">
                <Text size="labelStrong">{t('NewestLabWork')}</Text>
                {activeHeroCards.length > 0 ? (
                  activeHeroCards.map(({ node, props }) => (
                    <LabHeroCard key={node.id} {...props} />
                  ))
                ) : (
                  <LabHeroCard kind="empty" />
                )}
              </div>
              <div className="space-y-xs  ">
                <div className="flex items-center justify-between">
                  <Text size="labelStrong">{t('PastLabs')}</Text>
                  {hasMoreLabs && (
                    <ButtonLink
                      to={labsRoutes.pastLabs.build()}
                      intent="ghost"
                      size="small"
                      width="fit"
                    >
                      {t('ViewAll')}
                    </ButtonLink>
                  )}
                </div>
                <PastLabs labWorks={displayedLabWorks} />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="pt-xl">
        <LabSupport />
      </div>
    </div>
  );
};

const NoLabWorkYet = () => {
  const { t } = useTranslation('labs', { keyPrefix: 'NoLabWork' as const });

  return (
    <div className="flex-col items-center justify-center text-center w-full space-y-xs mt-[46px]">
      <IconTile icon="labs" size="large" intent="info" />
      <Text size="headingXS">{t('Title')}</Text>
      <Text size="bodyS" color="contentSubdued">
        {t('Description')}
      </Text>
    </div>
  );
};
